import type { TicketAudioZustand, TicketTelefonie } from "./ticketTypen";

/**
 * Telefonie-Diagnose im Ticket (T-28) — nur für Admin/GF/GL (wie „Technische
 * Spuren"). Kompakt: Stand beim Melden, Anrufe (Anrufliste + Softphone-Sicht)
 * und die [telephony]-Konsolenspuren.
 */
const uhr = (iso: string | null | undefined): string => {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const sek = (s: number | null | undefined): string => (s === null || s === undefined ? "–" : `${s} s`);

const MIKROFON: Record<string, string> = {
  granted: "freigegeben", denied: "gesperrt", prompt: "wird erfragt", unbekannt: "unbekannt",
};

export function audioKurz(a: TicketAudioZustand | null | undefined): string {
  if (!a) return "kein Audio-Element";
  const teile = [
    a.hatStream ? `Tracks ${a.tracksLive}/${a.tracksGesamt} live` : "kein Stream",
    a.paused ? "pausiert" : "spielt",
    `readyState ${a.readyState}`,
  ];
  if (a.muted) teile.push("stumm");
  if (a.sinkId) teile.push(`Ausgabe ${a.sinkId === "default" ? "Standard" : "gewählt"}`);
  if (a.playFehler) teile.push(`play-Fehler: ${a.playFehler}`);
  return teile.join(" · ");
}

export function TelefonieBlock({ telefonie: t }: { telefonie: TicketTelefonie }) {
  const stand = [
    t.sdk ? `Softphone: ${t.sdk.status}${t.sdk.fehler ? ` (${t.sdk.fehler})` : ""}` : "Softphone: –",
    `Nebenstelle: ${t.nebenstelle ?? "–"}`,
    `Mikrofon: ${t.mikrofon ? MIKROFON[t.mikrofon] ?? t.mikrofon : "–"}`,
    t.geraete ? `Geräte: ${t.geraete.eingaenge} Eingänge, ${t.geraete.ausgaenge} Ausgänge` : "Geräte: –",
  ];
  return (
    <section className="tk-abschnitt tk-telefonie">
      <h3>Telefonie</h3>
      <p className="tk-telefonie-stand">{stand.join(" · ")}</p>
      {t.audio && <p className="tk-leise">Audio beim Melden: {audioKurz(t.audio)}</p>}
      {t.unvollstaendig && <p className="tk-leise">Unvollständig erfasst (Zeitüberschreitung beim Melden).</p>}

      {!!t.anrufe?.length && (
        <>
          <h4 className="tk-verlauf-kopf">Anrufe der letzten 15 Minuten (Anrufliste)</h4>
          <ul className="tk-telefonie-liste">
            {t.anrufe.map((a) => (
              <li key={a.id}>
                <span className="tk-leise">{uhr(a.start)}</span> #{a.id} · {a.richtung === "outbound" ? "ausgehend" : a.richtung === "inbound" ? "eingehend" : a.richtung}
                {" "}· {a.nummer ?? "–"} · {a.status ?? "–"} · Dauer {sek(a.dauerSek)} · Gespräch {sek(a.gespraechSek)}
              </li>
            ))}
          </ul>
        </>
      )}

      {!!t.softphoneAnrufe?.length && (
        <>
          <h4 className="tk-verlauf-kopf">Softphone-Sicht (dieser Browser-Tab)</h4>
          <ul className="tk-telefonie-liste">
            {t.softphoneAnrufe.map((a) => (
              <li key={a.callId}>
                <span className="tk-leise">{uhr(a.start)}</span> {a.richtung === "outbound" ? "ausgehend" : "eingehend"} · {a.nummer ?? "–"} · {a.zustand}
                {" "}· angenommen {uhr(a.angenommen)} · Ende {uhr(a.ende)} · Tracks max. {a.tracksLiveMax}/{a.tracksMax} live
                {a.keinTon && <strong className="tk-telefonie-warnung"> · Kein Ton: {a.keinTon}</strong>}
                {a.endeGrund && <span className="tk-leise"> · Ende durch {a.endeGrund}</span>}
                {a.richtung === "inbound" && a.annehmenGeklickt !== undefined && (
                    <div className={a.annehmenErgebnis && a.annehmenErgebnis !== "angenommen" ? "tk-telefonie-warnung" : "tk-leise"}>
                        {a.annehmenGeklickt
                            ? `„Annehmen“ geklickt ${uhr(a.annehmenGeklickt)} → ${a.annehmenErgebnis ?? "ohne Ergebnis"}`
                            : "„Annehmen“ nicht geklickt"}
                    </div>
                )}
                {a.audioNachAnnahme && <div className="tk-leise">3 s nach Annahme: {audioKurz(a.audioNachAnnahme)}</div>}
              </li>
            ))}
          </ul>
        </>
      )}

      {!!t.spuren?.length && (
        <details>
          <summary>Telefonie-Spuren ({t.spuren.length})</summary>
          <pre className="tk-telefonie-spuren">
            {t.spuren.map((s) => `${uhr(s.zeit)} ${s.stufe.padEnd(5)} ${s.text}`).join("\n")}
          </pre>
        </details>
      )}
    </section>
  );
}
